import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./init";
import { convertTimestampToDate } from "./firestore";
import type { Tag } from "@/types/tag";

/**
 * 演員類型
 */
export interface Actor {
  id: string;
  name?: string;
  photo_url?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * 公司類型
 */
export interface Company {
  id: string;
  company_name?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * 商品類型
 */
export interface Product {
  id: string;
  name?: string;
  price?: number;
  coin_cost?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * 獲取所有標籤
 */
export async function getTags(): Promise<Tag[]> {
  try {
    const tagsRef = collection(db, "tags");
    let querySnapshot;
    let useClientSort = false;
    
    try {
      const q = query(tagsRef, orderBy("tw", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      // 如果 orderBy 失敗（可能沒有索引），使用基本查詢並在客戶端排序
      console.warn("Failed to order by 'tw', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(tagsRef));
      useClientSort = true;
    }

    const tags = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as Tag[];

    // 如果沒有使用 orderBy，在客戶端排序
    if (useClientSort) {
      tags.sort((a, b) => (a.tw || "").localeCompare(b.tw || ""));
    }

    return tags;
  } catch (error) {
    console.error("Error getting tags:", error);
    throw error;
  }
}

/**
 * 獲取所有演員
 */
export async function getActors(): Promise<Actor[]> {
  try {
    const actorsRef = collection(db, "actors");
    let querySnapshot;
    let useClientSort = false;
    
    try {
      const q = query(actorsRef, orderBy("name", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn("Failed to order by 'name', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(actorsRef));
      useClientSort = true;
    }

    const actors = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as Actor[];

    if (useClientSort) {
      actors.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return actors;
  } catch (error) {
    console.error("Error getting actors:", error);
    throw error;
  }
}

/**
 * 獲取所有公司
 */
export async function getCompanies(): Promise<Company[]> {
  try {
    const companiesRef = collection(db, "companies");
    let querySnapshot;
    let useClientSort = false;
    
    try {
      const q = query(companiesRef, orderBy("company_name", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn("Failed to order by 'company_name', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(companiesRef));
      useClientSort = true;
    }

    const companies = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as Company[];

    if (useClientSort) {
      companies.sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
    }

    return companies;
  } catch (error) {
    console.error("Error getting companies:", error);
    throw error;
  }
}

/**
 * 獲取所有商品
 */
export async function getProducts(): Promise<Product[]> {
  try {
    const productsRef = collection(db, "products");
    let querySnapshot;
    let useClientSort = false;
    
    try {
      const q = query(productsRef, orderBy("name", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn("Failed to order by 'name', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(productsRef));
      useClientSort = true;
    }

    const products = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as Product[];

    if (useClientSort) {
      products.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return products;
  } catch (error) {
    console.error("Error getting products:", error);
    throw error;
  }
}

